import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import ProtectedRoute from './components/ProtectedRoute';
import InitialRoute from './components/InitialRoute';
import './styles/App.css';

// Lazy load pages for better code splitting and performance
const HomePage = lazy(() => import('./pages/HomePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const ExercisePage = lazy(() => import('./pages/ExercisePage'));
const AssessmentPage = lazy(() => import('./pages/AssessmentPage'));
const AnalysisResultsPage = lazy(() => import('./pages/AnalysisResultsPage'));
const AnalysisHistoryPage = lazy(() => import('./pages/AnalysisHistoryPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const SEOPage = lazy(() => import('./pages/SEOPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Tools Pages
const WordCounterPage = lazy(() => import('./pages/tools/WordCounterPage'));
const LetterCounterPage = lazy(() => import('./pages/tools/LetterCounterPage'));
const CaseConverterPage = lazy(() => import('./pages/tools/CaseConverterPage'));
const SentenceCounterPage = lazy(() => import('./pages/tools/SentenceCounterPage'));

// Font Generator Tools
const FontGeneratorPage = lazy(() => import('./pages/tools/font-generator/FontGeneratorPage'));
const UnderlineTextPage = lazy(() => import('./pages/tools/font-generator/UnderlineTextPage'));
const BoldTextPage = lazy(() => import('./pages/tools/font-generator/BoldTextPage'));
const ItalicTextPage = lazy(() => import('./pages/tools/font-generator/ItalicTextPage'));
const StrikethroughTextPage = lazy(() => import('./pages/tools/font-generator/StrikethroughTextPage'));
const CursiveTextPage = lazy(() => import('./pages/tools/font-generator/CursiveTextPage'));
const GothicTextPage = lazy(() => import('./pages/tools/font-generator/GothicTextPage'));
const BubbleTextPage = lazy(() => import('./pages/tools/font-generator/BubbleTextPage'));
const SquareTextPage = lazy(() => import('./pages/tools/font-generator/SquareTextPage'));

// Loading fallback component
const PageLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '4px solid #f3f3f3',
      borderTop: '4px solid #667eea',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    }}></div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Navbar />
          <main className="main-content">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={
                  <InitialRoute>
                    <HomePage />
                  </InitialRoute>
                } />
                <Route path="/home" element={<HomePage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <ProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/assessment"
                  element={
                    <ProtectedRoute>
                      <AssessmentPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/exercises"
                  element={
                    <ProtectedRoute>
                      <ExercisePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analysis-results"
                  element={
                    <ProtectedRoute>
                      <AnalysisResultsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analysis-history"
                  element={
                    <ProtectedRoute>
                      <AnalysisHistoryPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/terms" element={<TermsOfServicePage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                {/* 404 Page */}
                <Route path="/404" element={<NotFoundPage />} />
                {/* Tools Pages */}
                <Route path="/word-counter" element={<WordCounterPage />} />
                <Route path="/letter-counter" element={<LetterCounterPage />} />
                <Route path="/case-converter" element={<CaseConverterPage />} />
                <Route path="/sentence-counter" element={<SentenceCounterPage />} />

                {/* Specialized Routes inside <Routes> block: */}
                {/* Font Generator Hub & Tools */}
                <Route path="/font-generator" element={<FontGeneratorPage />} />

                {/* Nested and Root-level SEO paths for indexing */}
                <Route path="/font-generator/bold-text" element={<BoldTextPage />} />
                <Route path="/font-generator/italic-text" element={<ItalicTextPage />} />
                <Route path="/font-generator/underline-text" element={<UnderlineTextPage />} />
                <Route path="/font-generator/strikethrough-text" element={<StrikethroughTextPage />} />
                <Route path="/font-generator/cursive-text" element={<CursiveTextPage />} />
                <Route path="/font-generator/gothic-text" element={<GothicTextPage />} />
                <Route path="/font-generator/bubble-text" element={<BubbleTextPage />} />
                <Route path="/font-generator/square-text" element={<SquareTextPage />} />

                {/* Legacy/Root-level SEO paths */}
                <Route path="/bold-text" element={<BoldTextPage />} />
                <Route path="/underline-text" element={<UnderlineTextPage />} />
                <Route path="/italic-text" element={<ItalicTextPage />} />
                <Route path="/strikethrough-text" element={<StrikethroughTextPage />} />
                <Route path="/cursive-text" element={<CursiveTextPage />} />
                <Route path="/gothic-text" element={<GothicTextPage />} />
                <Route path="/bubble-text" element={<BubbleTextPage />} />
                <Route path="/square-text" element={<SquareTextPage />} />

                {/* Dynamic SEO Routes */}
                <Route path="/:slug" element={<SEOPage />} />
                {/* Catch-all 404 route */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </main>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
